import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import {
  getLocalStorageJson,
  setLocalStorageJson,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { ANONYMOUS_CONCIERGE_CONVERSATIONS_LOCAL_STORAGE_KEY } from "../core/config";
import { isSupportV2ConversationId } from "./support-v2-conversation-id";

/**
 * Represents an anonymous conversation stored in localStorage.
 * These are conversations created before the user signs in.
 */
export interface AnonymousConversation {
  conversationId: string;
  token?: string;
}

/**
 * Get all anonymous conversations from localStorage.
 */
export function getAnonymousConversations(): AnonymousConversation[] {
  return (
    getLocalStorageJson(ANONYMOUS_CONCIERGE_CONVERSATIONS_LOCAL_STORAGE_KEY) ??
    []
  );
}

/**
 * Get just the conversation IDs from anonymous conversations.
 */
export function getAnonymousConversationIds(): string[] {
  return getAnonymousConversations().map((conv) => conv.conversationId);
}

/**
 * Anonymous-conversation storage is shared globally across every team this
 * browser has visited. When the active team is on support-v2, only ids that
 * pass the `ConversationId` schema (the same validator used by every
 * single-id v2 call via `ConversationId.make`) are valid input — legacy
 * Mongo-ObjectId-format ids belong to v1 teams and would crash the v2
 * server. Filter at the storage boundary so v2 callers can't accidentally
 * forward them.
 */
export function getAnonymousConversationIdsForV2(): ConversationId[] {
  return getAnonymousConversationIds().filter(isSupportV2ConversationId);
}

/**
 * Get the auth token for a specific anonymous conversation.
 */
export function getAnonymousConversationToken(
  conversationId: string,
): string | undefined {
  const conversations = getAnonymousConversations();
  return conversations.find((conv) => conv.conversationId === conversationId)
    ?.token;
}

/**
 * Add or update an anonymous conversation in localStorage.
 */
export function upsertAnonymousConversation(
  conversationId: string,
  token?: string,
): void {
  const conversations = getAnonymousConversations();
  const existingIndex = conversations.findIndex(
    (conv) => conv.conversationId === conversationId,
  );

  if (existingIndex >= 0) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    conversations[existingIndex].token = token;
  } else {
    conversations.push({ conversationId, token });
  }

  setLocalStorageJson(
    ANONYMOUS_CONCIERGE_CONVERSATIONS_LOCAL_STORAGE_KEY,
    conversations,
  );
}

/**
 * Clear all anonymous conversations from localStorage.
 * Typically called after user signs in and conversations are associated with their account.
 */
export function clearAnonymousConversations(): void {
  localStorage.removeItem(ANONYMOUS_CONCIERGE_CONVERSATIONS_LOCAL_STORAGE_KEY);
}
